/**
 * Middleware to check if user has platform admin access
 *
 * In single-tenant mode:
 * - Platform admin routes are completely disabled
 * - Users are redirected to home
 *
 * In multi-tenant mode:
 * - Only users with platformAccess flag can access platform routes
 * - Platform admins can access everything
 * - Trainers can only access their assigned tenants
 */

// eslint-disable-next-line import/no-cycle
import store from '@/store';

/**
 * Check if multi-tenant mode is enabled (from runtime config)
 */
export function isMultiTenantEnabled() {
  // Use Vuex store for runtime config (loaded from backend)
  return store.getters['runtimeConfig/isMultiTenantEnabled'] === true;
}

/**
 * Get platform user from localStorage
 */
export function getPlatformUser() {
  const userStr = localStorage.getItem('platform_user');
  if (userStr) {
    try {
      return JSON.parse(userStr);
    } catch (e) {
      return null;
    }
  }
  return null;
}

/**
 * Check if platform user is logged in
 */
export function isPlatformUserLoggedIn() {
  const token = localStorage.getItem('platform_token');
  const user = getPlatformUser();
  return !!(token && user);
}

/**
 * Check if current user has platform access
 */
export function hasPlatformAccess() {
  return isPlatformUserLoggedIn();
}

/**
 * Check if current user is a platform admin
 */
export function isPlatformAdmin() {
  const user = getPlatformUser();
  return user?.role === 'platform_admin';
}

/**
 * Check if current user is a platform trainer
 */
export function isPlatformTrainer() {
  const user = getPlatformUser();
  return user?.role === 'platform_trainer';
}

/**
 * Get assigned tenants for the current user (for trainers)
 */
export function getAssignedTenants() {
  const user = getPlatformUser();
  return user?.assignedTenants || [];
}

/**
 * Route guard for platform admin routes
 */
export function checkPlatformAccess(to, from, next) {
  // In single-tenant mode, platform routes are disabled
  if (!isMultiTenantEnabled()) {
    console.warn('Platform admin is disabled in single-tenant mode');
    next('/');
    return;
  }

  // Check if platform user is logged in
  if (!isPlatformUserLoggedIn()) {
    next('/platform/login');
    return;
  }

  // User has platform access, allow navigation
  next();
}

/**
 * Route guard that also checks for admin role specifically
 */
export function checkPlatformAdminOnly(to, from, next) {
  // First check basic platform access
  if (!isMultiTenantEnabled()) {
    next('/');
    return;
  }

  if (!isPlatformUserLoggedIn()) {
    next('/platform/login');
    return;
  }

  // Must be a platform admin (not just trainer)
  if (!isPlatformAdmin()) {
    console.warn('Only platform admins can access this route');
    next('/platform');
    return;
  }

  next();
}

export default {
  isMultiTenantEnabled,
  hasPlatformAccess,
  isPlatformAdmin,
  isPlatformTrainer,
  getAssignedTenants,
  checkPlatformAccess,
  checkPlatformAdminOnly,
};
